import { useCallback } from 'react';
import { 
    getPersonas as getPersonasApi,
    getPersona as getPersonaApi,
    updatePersona as updatePersonaApi,
    createPersona as createPersonaApi,
    deletePersona as deletePersonaApi,
    Persona
} from '../api/Persona'
import { useUserInfoContext } from '../contexts/UserInfoContext';

export const usePersona = () => {
    const { userInfo } = useUserInfoContext()

    const requireUserInfo = () => {
        if (!userInfo) throw new Error('AUTH_REQUIRED');
        return userInfo;
    };

    const getPersonas = useCallback(async () => {
        try {
            return await getPersonasApi(requireUserInfo());
        } catch (error) {
            throw error
        }
    }, [userInfo]);

    const getPersona = useCallback(async (personaId: string) => {
        try {
            return await getPersonaApi(requireUserInfo(), personaId);
        } catch (error) {
            throw error
        }
    }, [userInfo]);

    const updatePersona = useCallback(async (personaId: string, updates: Partial<Persona>) => {
        try {
            return await updatePersonaApi(userInfo, personaId, updates);
        } catch (error) {
            throw error
        }
    }, [userInfo]);

    const deletePersona = useCallback(async (personaId: string) => {
        try {
            return await deletePersonaApi(userInfo, personaId);
        } catch (error) {
            throw error
        }
    }, [userInfo]);

    const updatePersonaTemperature = useCallback(async (personaId: string, temperature: number) => {
        try {
            const updates = {
                temperature
            };

            return await updatePersonaApi(userInfo, personaId, updates);
        } catch (error) {
            throw error
        }
    }, [userInfo]);

    const updatePersonaResponseMaxTokens = useCallback(async (personaId: string, responseMaxTokens: number) => {
        try {
            const updates = {
                // temperature is stored as a string in dynamodb
                responseMaxTokens
            };

            return await updatePersonaApi(userInfo, personaId, updates);
        } catch (error) {
            throw error
        }
    }, [userInfo]);

    const updatePersonaPersonality = useCallback(async (personaId: string, personality: string) => {
        try {
            const updates = {
                personality
            };

            return await updatePersonaApi(userInfo, personaId, updates);
        } catch (error) {
            throw error
        }
    }, [userInfo]);

    const updatePersonaImageURL = useCallback(async (personaId: string, imageURL: string) => {
        try {
            const updates = {
                imageURL
            };
            return await updatePersonaApi(userInfo, personaId, updates);
        } catch (error) {
            throw error
        }
    }, [userInfo]);

    const createPersona = useCallback(async (persona: Persona) => {
        try {
            return await createPersonaApi(userInfo, persona);
        } catch (error) {
            throw error
        }
    }, [userInfo]);

    return {
        getPersonas,
        getPersona,
        updatePersona,
        deletePersona,
        updatePersonaTemperature,
        updatePersonaResponseMaxTokens,
        updatePersonaImageURL,
        updatePersonaPersonality,
        createPersona
    }
}

export const usePersonaDynamo = () => {
    const { userInfo } = useUserInfoContext()

    const requireUserInfo = () => {
        if (!userInfo) throw new Error('AUTH_REQUIRED');
        return userInfo;
    };

    const getPersonas = useCallback(async () => {
        try {
            return await getPersonasApi(requireUserInfo());
        } catch (error) {
            throw error
        }
    }, [userInfo]);

    const updatePersonaTemperature = useCallback(async (personaId: string, temperature: number) => {
        try {
            const updates = {
                temperature
            };

            return await updatePersonaApi(userInfo, personaId, updates);
        } catch (error) {
            throw error
        }
    }, [userInfo]);

    const updatePersonaResponseMaxTokens = useCallback(async (personaId: string, responseMaxTokens: number) => {
        try {
            const updates = {
                // temperature is stored as a string in dynamodb
                responseMaxTokens
            };

            return await updatePersonaApi(userInfo, personaId, updates);
        } catch (error) {
            throw error
        }
    }, [userInfo]);

    const updatePersonaPersonality = useCallback(async (personaId: string, personality: string) => {
        try {
            const updates = {
                personality
            };

            return await updatePersonaApi(userInfo, personaId, updates);
        } catch (error) {
            throw error
        }
    }, [userInfo]);

    const updatePersonaImageURL = useCallback(async (personaId: string, imageURL: string) => {
        try {
            const updates = {
                imageURL
            };
            return await updatePersonaApi(userInfo, personaId, updates);
        } catch (error) {
            throw error
        }
    }, [userInfo]);

    const createPersona = useCallback(async (persona: Persona) => {
        try {
            return await createPersonaApi(userInfo, persona);
        } catch (error) {
            throw error
        }
    }, [userInfo]);

    return {
        getPersonas,
        updatePersonaTemperature,
        updatePersonaResponseMaxTokens,
        updatePersonaImageURL,
        updatePersonaPersonality,
        createPersona
    }
}
